import { skipToken } from '@reduxjs/toolkit/query';
import Loader from 'components/Loader.tsx';
import NoData from 'components/NoData.tsx';
import BasicTable from 'components/tables/BasicTable.tsx';
import type { DataTableColumnDef } from 'components/tables/data-table';
import LinkWithOrigin from 'components/util/LinkWithOrigin.tsx';
import { format } from 'date-fns';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useGetCoiChecksWithFiltersQuery } from 'store/api/coi-check.ts';
import { CoiCheckListItem, CoiCheckScopeType } from 'types/coi-check.ts';
import { t } from 'utils/getTranslations.ts';
import { ENTITY_LINK_CLASS } from '../util/CoiCheckUtil.ts';

interface CoiViewProps {
    scope: CoiCheckScopeType;
    entityId?: string | number;
}

const CoiView: React.FC<CoiViewProps> = ({ scope, entityId }) => {
    const effectiveFormat = useEffectiveDateFormat();
    const { data, isLoading, isSuccess } = useGetCoiChecksWithFiltersQuery(
        entityId
            ? {
                  filters: `relations.coiCheckScopeTypeCode=in=(${scope});relations.isManuallySelected==true;relations.entityId=in=${entityId}`,
                  sort: ['id,asc'],
                  page: 0,
                  pageSize: 2147483647,
              }
            : skipToken
    );

    const coiCheckColumns = useMemo<DataTableColumnDef<CoiCheckListItem>[]>(
        () => [
            {
                header: t('coi.searchTerm'),
                accessorKey: 'searchTerm',
                cell: ({ row }) => {
                    const { searchTerm, id } = row.original;
                    return id ? (
                        <LinkWithOrigin className={ENTITY_LINK_CLASS} to={`/coi-check/${id}`}>
                            {searchTerm}
                        </LinkWithOrigin>
                    ) : null;
                },
            },
            {
                header: t('coi.time'),
                accessorKey: 'checkedAt',
                cell: ({ getValue }) => {
                    const checkedAt = getValue<CoiCheckListItem['checkedAt']>();
                    return checkedAt ? format(new Date(checkedAt), `${effectiveFormat} HH:mm`) : '';
                },
            },
            {
                header: t('coi.performed-by'),
                id: 'checkedByEmployee.name',
                accessorFn: (row) => row.checkedByEmployee?.name,
                cell: ({ getValue }) => getValue<CoiCheckListItem['checkedByEmployee']['name']>() ?? '',
            },
        ],
        [effectiveFormat]
    );

    if (isLoading) {
        return <Loader />;
    }

    if (!isSuccess) {
        return null;
    }

    if (data.content.length === 0) {
        return <NoData titleId={'coi.no-coi-checks-found'} />;
    }

    return (
        <div className="flex h-full flex-col gap-4">
            <h5 className="text-h5">
                <FormattedMessage id={'coi.conflict-check'} />
            </h5>
            <BasicTable data={data.content} columns={coiCheckColumns} />
        </div>
    );
};

export default CoiView;
